import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { NotificationsService } from '@services/notifications.service';
import { NotificationsModel } from '@models/notifications.model';
import { HandleNotificationClickService } from '@shared/services/handle-notification-click.service';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { FCMService } from '@shared/services/fcm.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { NotificationStatusEnum } from '@shared/models/notifications.model';
@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.css'],
    standalone: false
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @ViewChild(NgbDropdown) dropdown: NgbDropdown;
  @Input() disabled: boolean = false;
  message: string | null = null;
  notification_count: number = 0;
  isBrowser: boolean = false;
  notifications: NotificationsModel[] = [];
  loading: boolean = false;
  created_at: string = '';
  formattedDate: string = '';
  notread: boolean = false;
  page: number = 1; // Keep track of the current page
  perPage: number = 10; // Number of notifications to load per page
  hasMore: boolean = true; 
  totalNotifications: number = 0;
  type: string;
  isFirstTime : boolean = true;
  isScroll : boolean = false;
  // Subscription for the FCM notification observable
  private fcmSubscription!: Subscription;
  private notificationsSubscription!: Subscription;

  constructor(
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
    public language: LanguageService,
    private fcmService: FCMService,
    private handleNotificationClickService: HandleNotificationClickService
  ) {}
  ngOnInit(): void {
    this.notifications= [];
    this.isFirstTime = true;
    this.loadnotifications();
    this.notificationsSubscription = this.notificationsService.notifications$.subscribe({
      next: res => {
        this.notification_count = this.notificationsService.getNotificationsBadgeCount(res);
        this.notread = res.data.some(n => !n.read);
      },
    });
      // Subscribe to the FCM notification observable
      this.fcmSubscription = this.fcmService.getNotificationObservable().subscribe({
      next: () => {
        this.refreshFromRealtimeEvent();
      },
      error: (error) => {
        console.error('Error receiving FCM notification', error);
      },
    });
  }

  ngOnDestroy(): void {
    // Clean up the subscription when the component is destroyed
    if (this.fcmSubscription) {
      this.fcmSubscription.unsubscribe();
    }
    if (this.notificationsSubscription) {
      this.notificationsSubscription.unsubscribe();
    }
  }

  loadnotifications(reset: boolean = false, showLoader: boolean = true) {
    if (this.disabled || this.loading) {
      return;
    }

    if (reset) {
      this.page = 1;
      this.notifications = [];
      this.hasMore = true;
      this.totalNotifications = 0;
      this.isScroll = false;
      this.isFirstTime = true;
    }
    if (this.hasMore && this.isFirstTime || this.hasMore && this.isScroll) {
      if (showLoader && this.notifications.length < 1) {
        this.loading = true;
      }
      this.notificationsService.show_all_notifications(this.page, this.perPage).subscribe({
        next: res => {
          this.isFirstTime = false;
          this.notifications = [...this.notifications, ...this.getActiveNotifications(res.data)];
          this.notification_count = this.notificationsService.getNotificationsBadgeCount(res);
          this.totalNotifications = res.meta.pagination.total;
          if(this.notifications.length < this.totalNotifications) {
            this.hasMore = true;
          } else {
            this.hasMore = false;
          }
          this.notread = this.notifications.some(n => !n.read);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
    } 
  }
  
  onScroll(event: Event) {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    // Check if scrolled to the bottom
    if (scrollTop + clientHeight >= scrollHeight - 1) {
      this.isScroll = true;
      this.page++;
      this.loadnotifications(); // Load next page
    }
  }

  mark_all_as_read() {
    this.notificationsService.mark_all_as_read().subscribe({
      next: () => {
        this.refreshNotificationsSilently();
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  formatLocalDate(dateString: string) {
    const language = this.translateService.currentLang;
    const utcMoment = moment.utc(dateString);
    const localMoment = utcMoment.local();
    localMoment.locale(language);
    return localMoment;
  }

  handleNotificationClick(notification: NotificationsModel) {
    this.handleNotificationClickService.handleNotificationClick(notification);
    this.dropdown.close();
  }

  onDropdownOpenChange(isOpen: boolean) {
    if (isOpen) {
      if (this.notifications.length > 0) {
        this.refreshNotificationsSilently();
      } else {
        this.loadnotifications(true);
      }
      return;
    }

    this.page = 1;
    this.hasMore = true;
    this.isFirstTime = true;
    this.isScroll = false;
  }

  private refreshFromRealtimeEvent(): void {
    this.refreshNotificationsSilently();
  }

  private refreshNotificationsSilently(): void {
    if (this.disabled || this.loading) {
      return;
    }

    const loadedItems = Math.max(this.notifications.length, this.perPage);
    this.notificationsService.show_all_notifications(1, loadedItems).subscribe({
      next: res => {
        this.page = 1;
        this.isFirstTime = false;
        this.isScroll = false;
        this.notifications = this.getActiveNotifications(res.data);
        this.notification_count = this.notificationsService.getNotificationsBadgeCount(res);
        this.totalNotifications = res.meta.pagination.total;
        this.hasMore = this.notifications.length < this.totalNotifications;
        this.notread = this.notifications.some(n => !n.read);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getNotificationType(notification: NotificationsModel): 'pending' | 'assigned' {
    return Number(notification?.status?.key) === NotificationStatusEnum.ASSIGNED ? 'assigned' : 'pending';
  }

  private getActiveNotifications(notifications: NotificationsModel[]): NotificationsModel[] {
    return notifications.filter(notification => Number(notification?.status?.key) !== NotificationStatusEnum.DONE);
  }
}
